import React from 'react';
import styled from 'styled-components';
import { Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useDirection } from '~/hooks/i18nHooks';

// Detect which services are enabled from the payload
function detectServiceTypes(payload: any) {
  const services = payload?.services;
  const isPOS = !!(services?.POS?.enabled || services?.nGeniusGo?.enabled);
  const isEcommerce = !!(services?.eCommerce?.enabled);
  const posDeviceCount =
    services?.POS?.terminals ?? services?.nGeniusGo?.terminalFee ?? 1;
  const isPOSMultiple = isPOS && posDeviceCount > 1;

  return { isPOS, isEcommerce, isPOSMultiple };
}

function FieldRow({
  label,
  value,
  isMobile,
}: {
  label: string;
  value?: string | null;
  isMobile: boolean;
}) {
  if (!isMobile) {
    // Desktop: label on top, bold value below (each field is one grid cell)
    return (
      <DesktopCell>
        <Typography.Paragraph className="labelKey">{label}</Typography.Paragraph>
        <Typography.Paragraph className="value">{value}</Typography.Paragraph>
      </DesktopCell>
    );
  }
  // Mobile: label | value side by side
  return (
    <Row gutter={[16, 16]} className="labelKeyRow">
      <Col span={12}>
        <Typography.Paragraph className="labelKey smallText">{label}</Typography.Paragraph>
      </Col>
      <Col span={12}>
        <Typography.Paragraph className="value">{value}</Typography.Paragraph>
      </Col>
    </Row>
  );
}

function BusinessDetails(props: any) {
  const { data, payload } = props;
  const { t } = useTranslation('ksaApplication');
  const isMobile = useIsMobile();
  const dir = useDirection();

  const { isPOS, isEcommerce } = detectServiceTypes(payload);
  const showWebsite = isEcommerce;
  const showStoreAddress = isPOS;

  const businessDetails = data?.businessDetails ?? {};

  return (
    <>
      <SectionTitle level={4}>
        {t('Business Details')}
      </SectionTitle>
      <Box dir={dir} isMobile={isMobile}>
        {/* Merchant Name (English) — always shown */}
        <FieldRow
          isMobile={isMobile}
          label={t('Merchant Name (English)')}
          value={businessDetails.merchantNameEnglish ?? data?.merchantNameEn}
        />

        {/* Merchant Name (Arabic) — always shown */}
        <FieldRow
          isMobile={isMobile}
          label={t('Merchant Name (Arabic)')}
          value={businessDetails.merchantNameArabic ?? data?.merchantNameAr}
        />

        {/* Company Website — Ecommerce only / Ecommerce + POS */}
        {showWebsite && (
          <FieldRow
            isMobile={isMobile}
            label={t('Company Website')}
            value={data?.websiteUrl}
          />
        )}

        {/* Nature of Business — always shown */}
        <FieldRow
          isMobile={isMobile}
          label={t('Nature of Business or Main Line of Business')}
          value={data?.businessNatureKey || data?.mccGroup}
        />

        {/* Tax Registration Number — always shown when present */}
        {data?.taxRegistrationCertificate?.number && (
          <FieldRow
            isMobile={isMobile}
            label={t('Tax Registration Number')}
            value={data?.taxRegistrationCertificate?.number}
          />
        )}

        {/* Store Address (English) — POS only / Ecommerce + POS */}
        {showStoreAddress && (
          <FieldRow
            isMobile={isMobile}
            label={t('Store Address (English)')}
            value={businessDetails.storeAddressEnglish ?? data?.storeAddressEn}
          />
        )}

        {/* Store Address (Arabic) — POS only / Ecommerce + POS */}
        {showStoreAddress && (
          <FieldRow
            isMobile={isMobile}
            label={t('Store Address (Arabic)')}
            value={businessDetails.storeAddressArabic ?? data?.storeAddressAr}
          />
        )}

        {/* City — POS only / Ecommerce + POS */}
        {showStoreAddress && (
          <FieldRow isMobile={isMobile} label={t('City')} value={businessDetails.city ?? data?.city} />
        )}
      </Box>
    </>
  );
}

const SectionTitle = styled(Typography.Title)`
  margin-bottom:15px !important;
`;

/* Desktop: one grid cell per field — label stacked above value */
const DesktopCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .labelKey,
  .value {
    margin-bottom: 0;
  }
`;

const Box = styled.div<any>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  background-color: ${({ isMobile }) => (isMobile ? '#ffffff' : '#F9F9F9')};
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  box-shadow: 0 2px 6px 0 rgba(0, 89, 150, 0.03), 0 -1px 8px -10px rgba(0, 89, 150, 0.03);

  /* ── Desktop: 2-column grid, each cell = one field (label + value stacked) ── */
  ${({ isMobile }) =>
    !isMobile &&
    `
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px 32px;
  `}

  /* ── Mobile: flex column, rows separated by thin dividers ── */
  ${({ isMobile }) =>
    isMobile &&
    `
    display: flex;
    flex-direction: column;
    gap: 0;

    .labelKeyRow {
      padding: 12px 0;
      margin: 0 !important;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
        padding-bottom: 0;
      }

      &:first-child {
        padding-top: 0;
      }
    }
  `}

  .smallText {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    margin-bottom: 0;
  }

  .labelKey {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.14px;
  }
  .value {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin-bottom: 0;

    @media (max-width: 576px) {
      text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
    }
  }
`;

export default BusinessDetails;
